import React from 'react'
import { Stack, useRouter } from 'expo-router'
import { Pressable, ScrollView, Text, View, useWindowDimensions } from 'react-native'
import { AppModal } from '../../../components/ui/AppModal'
import { useTheme } from '../../../hooks/useTheme'
import { DesktopHomeSample } from '../../../preview/DesktopHomeSample'
import { DESKTOP_HOME_CONCEPTS } from '../../../preview/desktopHomeConcepts'
import type { GameResponse } from '@warcarp/api-types'

export default function DesktopHomeConceptGallery() {
  const { width, height } = useWindowDimensions()
  const { theme, setTheme } = useTheme()
  const router = useRouter()
  const original = React.useRef(theme)
  React.useEffect(() => { const restore = original.current; return () => setTheme(restore) }, [setTheme])
  const [selected, setSelected] = React.useState(0)
  const [full, setFull] = React.useState(false)
  const [completed, setCompleted] = React.useState(false)
  const [preset, setPreset] = React.useState<'window' | '1024' | '1440'>('window')
  const [actionMessage, setActionMessage] = React.useState('')
  const [picked, setPicked] = React.useState<GameResponse | null>(null)
  const dark = theme === 'dark'
  const ink = dark ? '#e6e1c4' : '#18170f'
  const soft = dark ? '#b4b79e' : '#71674b'
  const edge = dark ? '#8aa14a' : '#4a5a2a'
  const canvas = dark ? '#1b2213' : '#eee3c1'
  const paper = dark ? '#2d351f' : '#f4ecd3'
  const columns = width >= 1280 ? 3 : width >= 820 ? 2 : 1
  const available = Math.min(width - 40, 1440)
  const tileWidth = (available - (columns - 1) * 20) / columns
  const sampleWidth = preset === 'window' ? width : Math.min(Number(preset), width)
  const buttonStyle = { minHeight: 36, paddingVertical: 8, paddingHorizontal: 12, borderWidth: 1, borderColor: edge, borderRadius: 7, backgroundColor: paper } as const
  const controls = <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
    <Pressable accessibilityRole="button" style={buttonStyle} onPress={() => setTheme(dark ? 'light' : 'dark')}><Text className="font-stencil" style={{ color: ink, fontSize: 10 }}>{dark ? 'Light mode' : 'Dark mode'}</Text></Pressable>
    <Pressable accessibilityRole="button" style={buttonStyle} onPress={() => setCompleted(value => !value)}><Text className="font-stencil" style={{ color: ink, fontSize: 10 }}>{completed ? 'Show current' : 'Show completed'}</Text></Pressable>
    {full && (['window', '1024', '1440'] as const).map(value => <Pressable key={value} accessibilityRole="button"
      accessibilityState={{ selected: preset === value }} style={buttonStyle} onPress={() => setPreset(value)}>
      <Text className="font-stencil" style={{ color: ink, fontSize: 10 }}>{value === 'window' ? 'Full window' : `${value}px`}</Text>
    </Pressable>)}
    {full && <Pressable accessibilityRole="button" style={buttonStyle} onPress={() => { setFull(false); setActionMessage(''); setPicked(null) }}><Text className="font-stencil" style={{ color: ink, fontSize: 10 }}>Back to gallery</Text></Pressable>}
  </View>
  const onAction = (action: string) => { setPicked(null); setActionMessage(`Sample action: ${action}`) }
  const onGamePress = (game: GameResponse) => { setPicked(game); setActionMessage(`Sample game: ${game.name}`) }
  return <>
    <Stack.Screen options={{ title: 'Desktop home · six concepts', headerStyle: { backgroundColor: canvas }, headerTintColor: ink }} />
    <ScrollView style={{ backgroundColor: canvas, display: full ? 'none' : 'flex' }} contentContainerStyle={{ padding: 20, gap: 20, alignItems: 'center' }}>
      <View style={{ width: available, gap: 12 }}>
        <Text className="font-stencil" style={{ color: ink, fontSize: 30 }}>Desktop · home base</Text>
        <Text className="font-typed" style={{ color: soft, fontSize: 16 }}>Six layouts using the shared game tiles and real map previews. Open a layout to inspect the full window, switch themes or view completed games. These are sample campaigns; no games are created by this gallery.</Text>
        {controls}
      </View>
      <View style={{ width: available, flexDirection: 'row', flexWrap: 'wrap', gap: 20 }}>
        {DESKTOP_HOME_CONCEPTS.map((concept, index) => <View key={concept.id} testID={`desktop-home-${concept.id}`}
          style={{ width: tileWidth, backgroundColor: paper, borderWidth: 1, borderColor: edge, borderRadius: 12, overflow: 'hidden' }}>
          <View style={{ padding: 16, gap: 8 }}>
            <Text className="font-stencil" style={{ color: ink, fontSize: 14 }}>{String(index + 1).padStart(2, '0')} · {concept.name}</Text>
            <Text className="font-typed" style={{ color: soft, fontSize: 14, minHeight: 54 }}>{concept.description}</Text>
          </View>
          <View pointerEvents="none" accessibilityElementsHidden importantForAccessibility="no-hide-descendants"
            style={{ height: tileWidth * 0.68, overflow: 'hidden' }}>
            <View style={{ width: 1200, height: 816, transformOrigin: 'top left', transform: [{ scale: tileWidth / 1200 }] }}>
              <DesktopHomeSample concept={concept} completed={completed} onTabChange={setCompleted} onAction={onAction} onGamePress={onGamePress} />
            </View>
          </View>
          <Pressable accessibilityRole="button" accessibilityLabel={`Open ${concept.name} full screen`}
            onPress={() => { setSelected(index); setFull(true); setActionMessage(''); setPicked(null) }} style={{ padding: 16, borderTopWidth: 1, borderColor: edge }}>
            <Text className="font-stencil" style={{ color: ink, fontSize: 11 }}>Open full-screen layout ↗</Text>
          </Pressable>
        </View>)}
      </View>
    </ScrollView>
    <AppModal visible={full} transparent={false} animationType="none" onRequestClose={() => setFull(false)}>
      <View style={{ flex: 1, backgroundColor: canvas }}>
        <View style={{ padding: 12, gap: 8, borderBottomWidth: 1, borderColor: edge }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <Text className="font-stencil" style={{ color: ink, fontSize: 13 }}>QA · {String(selected + 1).padStart(2, '0')} · {DESKTOP_HOME_CONCEPTS[selected]!.name}</Text>
            {controls}
            <Pressable accessibilityRole="button" style={buttonStyle} onPress={() => { setSelected(value => (value + 1) % DESKTOP_HOME_CONCEPTS.length); setActionMessage(''); setPicked(null) }}><Text className="font-stencil" style={{ color: ink, fontSize: 10 }}>Next layout →</Text></Pressable>
          </View>
          {actionMessage ? <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <Text className="font-typed" style={{ color: soft, fontSize: 13 }}>{actionMessage}</Text>
            {picked?.mapId && <Pressable accessibilityRole="link" onPress={() => { setFull(false); router.push(`/(app)/maps/${picked.mapId}`) }}>
              <Text className="font-typed" style={{ color: ink, fontSize: 13 }}>Explore this map →</Text>
            </Pressable>}
          </View> : null}
        </View>
        <View style={{ flex: 1, width: sampleWidth, maxHeight: height, alignSelf: 'center' }}>
          <DesktopHomeSample key={DESKTOP_HOME_CONCEPTS[selected]!.id} concept={DESKTOP_HOME_CONCEPTS[selected]!} completed={completed} onTabChange={setCompleted} onAction={onAction} onGamePress={onGamePress} />
        </View>
      </View>
    </AppModal>
  </>
}
